import React, { useEffect, useState, useRef, useMemo } from 'react';
import { getAlerts } from '../../../../../../store/dashboard/AlertEngine/alertEngine_apiHelpers';
import styles from './AlertEngineTable.module.scss';
import { isEmpty, toTitleCase } from '../../../../../../utils/utils';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import A4Page from '../../../../../AicaPDFReport/A4Page/A4Page';
import clsx from 'clsx';
import { reportSectionsToShow } from '../../../../../AicaPDFReport/AicaPDFReport';
import { REPORT_SECTION_NAMES } from '../../../../../../store/pdfReport/pdfReport_constants';
import { caretRight } from '../../../../../../assets/hostedassets';
import { alertsConst } from '../../../../DashboardModules/RelatedParties/RelatedPartiesAlerts/relatedPartyAlerts';
import { getBannerStatusColors, getEdgeTypeLabels, getImage } from '../../../../DashboardModules/RelatedParties/relatedParty_constants';
import { ALERT_ROW_SOURCE, ENTITY_VISUAL_TYPE } from '../../../../../../utils/constants/commonConstants';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { AICA_ALERT_DETAIL_CLICKED } from '../../../../../../utils/constants/mixpanelEvents/investorEvents';

type AlertEngineTableProps = {
    assesseOrgId: string;
    parentOrgId: string;
    isPDFReport?: boolean;
    /** Shown in Entity column for company-sourced alerts when API omits entityName (e.g. assessed org). */
    assesseeDisplayName?: string;
};

type AlertRow = {
    severity: string;
    description: string;
    category: string;
    alertDate: string;
    source?: string;
    entityName?: string;
    entityType: string;
    type?: string;
    degree?: number;
};

type AlertSection = {
    title: string;
    rows: AlertRow[];
};

/** True when the alert is attributed to the assessed company (or source is unset). */
function isCompanySourceRow(row: AlertRow): boolean {
    return row.source === ALERT_ROW_SOURCE.COMPANY || row.source == null || row.source === '';
}

/** Resolves the entity name shown in the Entity column (API value or assessee fallback for company rows). */
function getEntityLabelForRow(row: AlertRow, assesseeDisplayName?: string): string | undefined {
    if (row.entityName?.trim()) return row.entityName.trim();
    if (isCompanySourceRow(row) && assesseeDisplayName?.trim()) return assesseeDisplayName.trim();
    return undefined;
}

/** Groups flat alert rows into collapsible sections: Company, then related-party buckets by type. */
function buildAlertSections(rows: AlertRow[]): AlertSection[] {
    const companyRows = rows.filter(isCompanySourceRow);
    const rpRows = rows.filter((r) => {
        if (r.source !== ALERT_ROW_SOURCE.RELATED_PARTY) return false;
        const severity = (alertsConst as any)[r.description]?.severity ?? '';
        return severity === 'High' || severity === 'Severe';
    });

    const sections: AlertSection[] = [];
    if (companyRows.length) {
        sections.push({ title: 'Company', rows: companyRows });
    }

    const byType = new Map<string, AlertRow[]>();
    for (const row of rpRows) {
        const key = row.entityType;
        if (!byType.has(key)) byType.set(key, []);
        byType.get(key)!.push(row);
    }

    for (const [entityType, list] of byType) {
        sections.push({ title: getEdgeTypeLabels(entityType), rows: list });
    }

    return sections;
}

/** Categories omitted from the PDF alerts table (shown in primary dashboard areas instead). */
const primaryAlerts = ['Banking', 'Bureau', 'EWS', 'Reconciliation', 'Revenue'];

/**
 * Paginated alerts table with expandable sections (UI) or a flat list (PDF).
 * Loads more pages on scroll; related-party rows use `alertsConst` for label/category.
 */
const AlertEngineTable = (props: AlertEngineTableProps) => {
    const { assesseOrgId, parentOrgId, isPDFReport = false, assesseeDisplayName } = props;
    const [totalRecords, setTotalRecords] = useState(0);
    const [companyCount, setCompanyCount] = useState(0);
    const [rowData, setRowData] = useState<AlertRow[]>([]);
    const [isAlertGenerated, setAlertGenerated] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState(1);
    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const initialCompanyExpandDecidedRef = useRef(false);
    const [isFetching, setIsFetching] = useState(false);
    const [expandedSections, setExpandedSections] = useState<string[]>([]);

    const alertSections = useMemo(() => buildAlertSections(rowData), [rowData]);

    /** Expands or collapses a section by its header title. */
    const toggleSection = (title: string) => {
        const isExpanding = !expandedSections.includes(title);
        if (isExpanding) Mixpanel.track(AICA_ALERT_DETAIL_CLICKED, { section: title });
        setExpandedSections((prev) => (isExpanding ? [...prev, title] : prev.filter((t) => t !== title)));
    };

    /** Human-readable description for related-party alerts keyed by API `description` code. */
    const getRPAlertDescription = (description: string): string => (alertsConst as any)[description]?.description ?? description;

    /** Category label for related-party alerts from `alertsConst`, else raw category. */
    const getRPAlertCategory = (description: string): string => (alertsConst as any)[description]?.category ?? description;

    /** Text and background colors for the severity pill by level. */
    const getBannerStatusColors = (status: string) => {
        let color = '';
        let banner = '';
        switch (status) {
            case 'SEVERE':
                color = '#FFFFFF';
                banner = '#DF666A';
                break;
            case 'HIGH':
                color = '#DF666A';
                banner = '#FDF0EC';
                break;
            case 'MEDIUM':
                color = '#D6BB0A';
                banner = '#FFFAD7';
                break;
            case 'LOW':
                color = '#00AA00';
                banner = '#E5FCDE';
                break;
        }
        return {
            color,
            banner,
        };
    };

    /** Fetches one page of alerts and appends to `rowData`; updates totals and generation flag. */
    const getTableData = async (page: number) => {
        setIsFetching(true);
        const payload = {
            assesseOrgId,
            parentOrgId,
            alertTypes: ['ALERT', 'EWS_ALERT'],
            pageNumber: page,
            pageSize: isPDFReport ? 30 : 20,
        };
        try {
            const res = await getAlerts(payload);
            if (res) {
                const newAlerts: AlertRow[] = Array.isArray(res.alerts) ? res.alerts : [];
                setRowData((prev) => [...prev, ...newAlerts]);
                setTotalRecords(res.totalRecords || 0);
                setAlertGenerated(res.alertsGenerated ?? false);
                if (res.companyCount != null) {
                    setCompanyCount(res.companyCount);
                }
            }
        } catch (error) {
            console.error('Error fetching alerts:', error);
        } finally {
            setIsFetching(false);
        }
    };

    /** Initial load: fetch page 1 on mount. */
    useEffect(() => {
        getTableData(pageNumber);
    }, []);

    /** Attaches infinite scroll: requests the next page when near the bottom of the table. */
    useEffect(() => {
        const handleScroll = () => {
            if (!tableWrapperRef.current || isFetching) return;

            const { scrollTop, scrollHeight, clientHeight } = tableWrapperRef.current;

            if (scrollTop + clientHeight >= scrollHeight - 10 && rowData.length < totalRecords) {
                setPageNumber((prevPage) => prevPage + 1);
            }
        };

        const tableWrapper = tableWrapperRef.current;
        if (tableWrapper) {
            tableWrapper.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (tableWrapper) {
                tableWrapper.removeEventListener('scroll', handleScroll);
            }
        };
    }, [isFetching, rowData, totalRecords]);

    /** After the first page, fetches additional pages when `pageNumber` increments from scroll. */
    useEffect(() => {
        if (pageNumber > 1) {
            getTableData(pageNumber);
        }
    }, [pageNumber]);

    /** Expand Company on first load only when there are no related-party alerts (otherwise stay collapsed). */
    useEffect(() => {
        if (initialCompanyExpandDecidedRef.current || isFetching || isEmpty(rowData)) return;

        const hasRelatedParty = rowData.some((r) => r.source === ALERT_ROW_SOURCE.RELATED_PARTY);
        const hasCompanyAlerts = rowData.some(isCompanySourceRow);

        if (!hasRelatedParty && hasCompanyAlerts) {
            setExpandedSections((prev) => (prev.includes('Company') ? prev : [...prev, 'Company']));
        }
        initialCompanyExpandDecidedRef.current = true;
    }, [rowData, isFetching]);

    /** Renders the styled severity badge for one row. */
    const renderSeverityCell = (row: AlertRow) => (
        <div
            style={{
                background: getBannerStatusColors(row.severity).banner,
                color: getBannerStatusColors(row.severity).color,
            }}
            className={styles.SeverityPill}
        >
            {row.severity}
        </div>
    );

    /** Renders the entity pill with icon and title-cased label, or empty when no label. */
    const renderEntityCell = (row: AlertRow) => {
        const label = getEntityLabelForRow(row, assesseeDisplayName);
        if (!label) return <div className={styles.Entity} />;
        return (
            <div className={styles.Entity} title={label}>
                <div className={styles.EntityPill}>
                    <img
                        src={getImage(isCompanySourceRow(row) ? ENTITY_VISUAL_TYPE.COMPANY : row.type!)}
                        alt=""
                        height={14}
                        width={14}
                        className={styles.EntityPillIcon}
                    />
                    <span className={styles.EntityPillText}>{toTitleCase(label) || label}</span>
                </div>
            </div>
        );
    };

    /** Empty state, PDF rows (excluding `primaryAlerts`), or expandable section rows for the dashboard. */
    const renderRows = () => {
        if (isEmpty(rowData)) {
            return (
                <div className={styles.Note}>
                    <div>{isAlertGenerated ? 'No alerts detected for this company' : 'No Alerts available'}</div>
                </div>
            );
        }

        if (isPDFReport) {
            return (
                <>
                    {rowData.map((row, index) =>
                        !primaryAlerts.includes(row?.category?.trim?.()) ? (
                            <div key={index} className={clsx(styles.Row, styles.RowWithEntity)}>
                                {renderSeverityCell(row)}
                                {renderEntityCell(row)}
                                <div className={styles.Description}>{row.description}</div>
                                <div className={styles.CategoryPill}>{row.category}</div>
                                <div className={styles.Date}>{row.alertDate}</div>
                            </div>
                        ) : (
                            <React.Fragment key={index} />
                        ),
                    )}
                </>
            );
        }

        return (
            <>
                {alertSections.map((section) => {
                    const isExpanded = expandedSections.includes(section.title);
                    return (
                        <div key={section.title} className={styles.Section}>
                            <button type="button" className={styles.SectionHeader} onClick={() => toggleSection(section.title)}>
                                <img
                                    className={clsx(styles.SectionChevron, {
                                        [styles.SectionChevronExpanded]: isExpanded,
                                    })}
                                    src={caretRight}
                                    alt=""
                                    height={14}
                                    width={14}
                                />
                                <span>
                                    {section.title} (
                                    {(section.title === 'Company' ? companyCount : section.rows.length).toString().padStart(2, '0')})
                                </span>
                                {section.title === 'Company' && isFetching && !isEmpty(rowData) && (
                                    <span className={styles.SectionPaginationSpinner} aria-hidden>
                                        <LoadingSpinner height="14px" color="#000000" style={{ margin: 0 }} />
                                    </span>
                                )}
                            </button>
                            {isExpanded &&
                                section.rows.map((row, index) => (
                                    <div key={`${section.title}-${index}`} className={clsx(styles.Row, styles.RowWithEntity)}>
                                        {renderSeverityCell(row)}
                                        {renderEntityCell(row)}
                                        <div className={styles.Description}>
                                            {row.source === ALERT_ROW_SOURCE.RELATED_PARTY
                                                ? getRPAlertDescription(row.description)
                                                : row.description}
                                        </div>
                                        <div className={styles.CategoryPill}>
                                            {row.source === ALERT_ROW_SOURCE.RELATED_PARTY
                                                ? getRPAlertCategory(row.description)
                                                : row.category}
                                        </div>
                                        <div className={styles.Date}>
                                            {row.source !== ALERT_ROW_SOURCE.RELATED_PARTY ? row.alertDate : ''}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    );
                })}
            </>
        );
    };

    /** Main table chrome: record count, column headers, scroll container, and body from `renderRows`. */
    const render = (
        <div
            className={clsx(styles.AlertEngineTableWeapper, {
                [styles.AlertEngineTableWeapper_PDFReport]: isPDFReport,
            })}
        >
            {!isPDFReport && isFetching && isEmpty(rowData) ? (
                <div className={styles.InitialLoadingOnly}>
                    <LoadingSpinner height={'3rem'} color={'#000000'} />
                </div>
            ) : (
                <>
                    <div className={styles.Count}>Showing {alertSections.reduce((sum, s) => sum + s.rows.length, 0)} records</div>
                    <div className={styles.TableWrapper} ref={tableWrapperRef}>
                        <div className={clsx(styles.Headers, styles.HeadersWithEntity)}>
                            <div>Severity</div>
                            <div>Entity</div>
                            <div>Description</div>
                            <div>Category</div>
                            <div>Date</div>
                        </div>
                        <div>{renderRows()}</div>
                    </div>
                </>
            )}
        </div>
    );

    if (isPDFReport)
        return reportSectionsToShow.includes(REPORT_SECTION_NAMES.ALERTS) && rowData?.length ? (
            <A4Page sectionName={REPORT_SECTION_NAMES.ALERTS}>
                <div className={styles.PDFCard}>
                    <div className={styles.Top}>
                        <div className={styles.Title}>{REPORT_SECTION_NAMES.ALERTS}</div>
                    </div>
                    <div className={styles.Body}>{render}</div>
                </div>
            </A4Page>
        ) : (
            <></>
        );
    else return render;
};

export default AlertEngineTable;
